/**
 * The manifest review's surface. Every type mirrors what
 * `services/manifest/manifestIntake.js` returns.
 *
 * The words below are the business's. A disposition is a decision a reviewer
 * records about one resident on one morning's manifest, and only one of them
 * — TWL_CONFIRMED — creates work for a Patient Advocate.
 */
import { apiRequest } from '../../services/api';

export type Disposition =
  | 'TWL_CONFIRMED' | 'NOT_TWL' | 'REPLACEMENT' | 'NEEDS_REVIEW'
  | 'DNQ' | 'PRIVATE_PAY' | 'MEDICAID_PENDING';

export type ReadinessState = 'READY' | 'HELD';

/** How each decision reads on the screen, and what it means to press it. */
export const DISPOSITION_LABEL: Record<Disposition, string> = {
  TWL_CONFIRMED: 'TWL — confirmed',
  NOT_TWL: 'Not a TWL',
  REPLACEMENT: 'Replacement',
  NEEDS_REVIEW: 'Needs review',
  DNQ: 'Does not qualify',
  PRIVATE_PAY: 'Private pay',
  MEDICAID_PENDING: 'Medicaid pending',
};

export const DISPOSITION_MEANS: Record<Disposition, string> = {
  TWL_CONFIRMED: 'The provider wrote this resident up as a TWL. This is the one decision that creates a lead.',
  NOT_TWL: 'On the manifest, but not written up as a TWL.',
  REPLACEMENT: 'A replacement device, not a first fitting. Recorded here; it does not open a new lead.',
  NEEDS_REVIEW: 'You looked and could not tell. Different from nobody having looked.',
  DNQ: 'Screened and does not qualify.',
  PRIVATE_PAY: 'Paying privately, not through Medicaid.',
  MEDICAID_PENDING: 'Medicaid is not in place yet.',
};

/** The colour family each decision carries, kept from the board's palette. */
export const DISPOSITION_TONE: Record<Disposition, 'go' | 'attn' | 'stop' | 'quiet'> = {
  TWL_CONFIRMED: 'go',
  NOT_TWL: 'quiet',
  REPLACEMENT: 'attn',
  NEEDS_REVIEW: 'attn',
  DNQ: 'stop',
  PRIVATE_PAY: 'quiet',
  MEDICAID_PENDING: 'attn',
};

/** The eMerge facts that put a building's day on the list (MAN-1, 2026-10-05). */
export type ManifestEvidence = 'VISIT_PLAN' | 'CHECK_IN' | 'REVIEW';

export const EVIDENCE_LABEL: Record<ManifestEvidence, string> = {
  VISIT_PLAN: 'visit completed in eMerge',
  CHECK_IN: 'provider checked in',
  REVIEW: 'review in eMerge',
};

/**
 * One building's day on the worklist, built from eMerge's own events only:
 * a visit plan Scheduling completed, a provider's check-in, or a review that
 * already exists. Never a Monday row.
 */
export type ManifestRow = {
  /** `${facility_id}:${visit_date}`. Optional only for a payload from before 2026-10-05. */
  key?: string;
  facility_id: string;
  facility_name: string;
  visit_date: string;
  evidence?: ManifestEvidence[];
  visit_plan_id?: string | null;
  check_in_id?: string | null;
  manifest_url: string | null;
  manifest_link_source?: 'REVIEW' | 'EMERGE' | null;
  provider: string | null;
  provider_employee_id?: string | null;
  /** Two completed plans that day named different providers; nobody is guessed. */
  provider_ambiguous?: boolean;
  /** Provenance only: the Monday item an operator linked to the review. Decides nothing. */
  scheduling_id: string | null;
  review_id: string | null;
  lines_total: number;
  twl_confirmed: number;
  undecided: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
};

/**
 * A Monday row with a manifest link and a last-visit date for which eMerge
 * holds no event. Reconciliation only: Monday sets that date when a planned
 * date arrives, so it does not prove a visit (CLINICAL_EVIDENCE_TRUTH).
 */
export type MondayReconciliationItem = {
  key: string;
  scheduling_id: string;
  monday_item_id: string | null;
  facility_id: string;
  facility_name: string;
  monday_visit_date: string;
  monday_manifest_url: string | null;
  monday_provider: string | null;
  source: 'MONDAY';
  proves_visit: false;
};

export type MondayReconciliation = {
  key: 'monday-reconciliation';
  label: string;
  source: 'MONDAY';
  what: string;
  items: MondayReconciliationItem[];
};

export type OpenOptions = {
  visitPlanId?: string | null;
  checkInId?: string | null;
  /** Provenance only: a Monday item of this building an operator links from the reconciliation lens. */
  schedulingId?: string | null;
  providerEmployeeId?: string | null;
  /** The reviewer's word that the visit took place. The API refuses a review without it (VISIT_NOT_CONFIRMED). */
  confirmed?: boolean;
};

export type ProviderChoice = { id: string; name: string; job_title: string | null };

/**
 * One building a VA can work (Isaiah Cotto, 2026-10-12): Active, or holding an
 * open review, or planned from 14 days ago onward. One row per building,
 * whatever its reviews. `review_*` are its CURRENT review (the open one, else
 * the newest closed one); every other review is in `earlier_reviews`.
 */
export type WorkableReviewRef = { id: string; visit_date: string; state: 'OPEN' | 'COMPLETED' };

export type WorkableRow = {
  /** The building's id: one row per building. */
  key: string;
  facility_id: string;
  facility_name: string;
  manifest_url: string | null;
  link_state: 'AVAILABLE' | 'CLEARED' | 'MISSING';
  last_visit: string | null;
  /** EMERGE: a plan or review eMerge holds. MONDAY: the mirror's date, a fallback that proves nothing. */
  last_visit_source: 'EMERGE' | 'MONDAY' | null;
  next_visit: string | null;
  next_visit_source: 'EMERGE' | 'MONDAY' | null;
  provider: string | null;
  review_id: string | null;
  review_state: 'OPEN' | 'COMPLETED' | null;
  review_visit_date: string | null;
  review_manifest_url: string | null;
  lines_total: number;
  twl_confirmed: number;
  undecided: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  earlier_reviews: WorkableReviewRef[];
  /** The newest visit eMerge recorded (completed plan or check-in) that has no review yet, with the ids that anchor it. */
  pending_visit: { visit_date: string; visit_plan_id: string | null; check_in_id: string | null } | null;
};

/** A building that is not in the table, found by name from "Find another building". */
export type OtherBuilding = { facility_id: string; facility_name: string; city: string | null; state: string | null };

export type ManifestIndex = {
  waiting: ManifestRow[];
  completed: ManifestRow[];
  /** The table's rows. The all-buildings `directory` of earlier builds is gone. */
  workable: { source: 'EMERGE'; items: WorkableRow[] };
  reconciliation?: MondayReconciliation;
  dispositions: Disposition[];
  /** The five words the business's own tally is written in. */
  twl_outcomes?: string[];
  can: { create_intake: boolean };
  /** 2026-11-03: Before the visit is lit (MANIFEST_PREVISIT is view or write). Absent from an older server: dark. */
  previsit_enabled?: boolean;
};

/**
 * episodeTruth (2026-10-05): whether a resident is a new lead at all. `line` is
 * the fact in the workforce's words ("Sold 07/20/26 · UMED approved"),
 * `message` the refusal sentence. Both null when the verdict is NEW_LEAD_OK.
 */
export type EpisodeVerdict = 'NEW_LEAD_OK' | 'ALREADY_SOLD' | 'UMED_IN_FLIGHT';
export type Episode = {
  verdict: EpisodeVerdict;
  sold_on: string | null;
  sale_reversed: boolean;
  umed_stage: string | null;
  umed_exception: string | null;
  umed_case_id: string | null;
  enrolled_case_id: string | null;
  enrolled_on: string | null;
  line: string | null;
  message: string | null;
};

export type ResidentLine = {
  id: string;
  patient_id: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  counselear_id: string | null;
  room: string | null;
  source_tags: string[];
  visit_outcome: string | null;
  notes: string | null;
  alerts: string | null;
  disposition: Disposition | null;
  decided_by: string | null;
  decided_at: string | null;
  has_open_case: boolean;
  already_assigned: boolean;
  /** Null when eMerge cannot say who this is. */
  episode?: Episode | null;
  /** 2026-10-26: a PA already closed this resident DNQ / NITWL / C-NITWL after this visit; TWL Q here opens no new lead. */
  already_worked?: AlreadyWorked | null;
  intake: { id: string; readiness_state: ReadinessState; reasons: string[] } | null;
  /**
   * 2026-10-27 (ManifestRounds.tsx): the provider's round that visit day. `seen` when the latest mark is SEEN (who, when,
   * and the provider's outcome that day); null otherwise. Absent on an older server.
   */
  seen?: { first_name: string; at: string | null; source: 'MANUAL' | 'TWL_Q' | string; outcome?: { key: string; label: string } | null } | null;
  /** Off the Current manifest now (screened DNQ, annual not due) and not added back: may be added back. */
  off_manifest?: boolean;
  /** Put back on the next manifest (20261027i): who and why. */
  added_back?: { reason: string | null; kind: 'PROVIDER' | 'OFFICE'; by: { first_name: string; mine: boolean }; at: string | null } | null;
  /** The review's "not seen" close named this line (20261027j): closed without a decision. */
  closed_not_seen?: boolean;
};

/** How the provider's round reads against a review (2026-10-27). Absent on an older server. */
export type ReviewRounds = {
  /** Seen marks are switched on (20261027c applied). */
  ready: boolean;
  /** "Add back" is switched on (20261027i applied). */
  add_back_ready: boolean;
  seen_lines: number;
  seen_undecided: number;
  not_seen_undecided: number;
  /** Undecided lines with no resident: never "not seen"; they block the not-seen close until decided. */
  unresolved_undecided?: number;
  /** The review was closed with its unseen residents "Not seen at visit": how many, by whom, when. */
  closed_not_seen: { count: number; by: string | null; at: string | null } | null;
  /** The provider marked someone seen at this visit, so the "not seen" close may be offered. */
  close_not_seen_available: boolean;
};

/**
 * ALREADY_WORKED (Isaiah Cotto, 2026-10-26): the office's tally and Manifest
 * Review do not open a second lead for a resident a PA already closed DNQ,
 * NITWL or C-NITWL after the visit day. `line` is the short fact ("Closed DNQ
 * 10/26/26 by a PA, after this visit"), `message` the sentence.
 */
export type AlreadyWorked = {
  disposition: 'DNQ' | 'NITWL' | 'C_NITWL';
  closed_on: string;
  line: string;
  message: string;
};

export type ReviewDetail = {
  review: {
    id: string;
    facility_id: string;
    facility_name: string;
    visit_date: string;
    manifest_url: string | null;
    provider: string | null;
    /** The eMerge provider a send-back goes to. null: nobody named yet (a send-back refuses). */
    provider_employee_id?: string | null;
    provider_basis?: 'VISIT_PLAN' | 'CHECK_IN' | 'REVIEWER' | null;
    visit_plan_id?: string | null;
    check_in_id?: string | null;
    /** Provenance only. */
    scheduling_id?: string | null;
    state: 'OPEN' | 'COMPLETED';
    /** Whether the workbook itself was read, or the lines are eMerge's own census. */
    snapshot_imported: boolean;
    completed_at: string | null;
    completed_by: string | null;
  };
  // withdrawn (20261029a): lines taken off this review as census echoes of a wrong placement; not in `lines`.
  counts: { lines: number; undecided: number; twl_confirmed: number; intakes: number; closed_not_seen?: number; withdrawn?: number };
  residents: ResidentLine[];
  rounds?: ReviewRounds;
};

export type IntakeResult = {
  intake_id: string;
  advocate_case_id: string;
  readiness_state: ReadinessState;
  reasons: string[];
  created: boolean;
};

const BASE = '/manifest-intake';

export const manifestApi = {
  index: (since?: string | null) =>
    apiRequest<ManifestIndex>('GET', since ? `${BASE}?since=${encodeURIComponent(since)}` : BASE),

  /**
   * Idempotent by (building, visit date): reopening joins the review in progress.
   * The visit is eMerge's (a completed plan or a check-in); a Monday item is only provenance.
   */
  open: (facilityId: string, visitDate: string, opts: OpenOptions = {}) =>
    apiRequest<ReviewDetail>('POST', `${BASE}/reviews`, {
      facility_id: facilityId, visit_date: visitDate,
      visit_plan_id: opts.visitPlanId || null, check_in_id: opts.checkInId || null,
      scheduling_id: opts.schedulingId || null, provider_employee_id: opts.providerEmployeeId || null,
      visit_confirmed: opts.confirmed === true,
    }),

  /** Who may be named as a visit's provider (active, credentialed). */
  providers: () => apiRequest<{ items: ProviderChoice[] }>('GET', `${BASE}/providers`),

  /** Name the provider who made this visit: the person a send-back reaches. */
  setProvider: (reviewId: string, employeeId: string) =>
    apiRequest<{ review_id: string; provider_employee_id: string; provider: string | null }>(
      'POST', `${BASE}/reviews/${reviewId}/provider`, { provider_employee_id: employeeId }
    ),

  detail: (reviewId: string) => apiRequest<ReviewDetail>('GET', `${BASE}/reviews/${reviewId}`),

  decide: (reviewId: string, lineId: string, disposition: Disposition, notes?: string) =>
    apiRequest<{ id: string; disposition: Disposition }>(
      'POST', `${BASE}/reviews/${reviewId}/residents/${lineId}`,
      notes === undefined ? { disposition } : { disposition, notes }
    ),

  addToIntake: (reviewId: string, lineId: string) =>
    apiRequest<IntakeResult>('POST', `${BASE}/reviews/${reviewId}/residents/${lineId}/intake`),

  /**
   * Close the review. `closeNotSeen` (2026-10-27): every resident the provider SAW is decided and the ones not seen close
   * as "Not seen at visit"; `expectedNotSeen` is the count the screen showed (a changed count is refused, nothing closes).
   */
  complete: (reviewId: string, opts: { closeNotSeen?: boolean; expectedNotSeen?: number } = {}) =>
    (opts.closeNotSeen
      ? apiRequest<{ id: string; completed_at: string; closed_not_seen?: number }>(
        'POST', `${BASE}/reviews/${reviewId}/complete`, { close_not_seen: true, expected_not_seen: opts.expectedNotSeen },
      )
      : apiRequest<{ id: string; completed_at: string; closed_not_seen?: number }>('POST', `${BASE}/reviews/${reviewId}/complete`)),

  /** Buildings in scope that are not in the table, by name: nothing under 2 characters, 10 at most. */
  searchOthers: (q: string) =>
    apiRequest<{ items: OtherBuilding[]; min: number; limit: number }>('GET', `${BASE}/facilities/search?q=${encodeURIComponent(q)}`),

  /** Buildings this reviewer may record a day for, by name. Scope still decides. */
  facilities: (q: string) =>
    apiRequest<{ items: FacilityOption[] }>('GET', `${BASE}/facilities?q=${encodeURIComponent(q)}`),

  /**
   * A whole day's TWL Q for one building, in one act.
   *
   * Isaiah Cotto, 2026-09-28: "The TWLQ list is the tally of that days
   * manifests qualified company wide." This is where that tally is entered,
   * and it reconstructs the manifest review it is a tally of.
   */
  recordDay: (body: {
    facility_id: string; visit_date: string; provider?: string | null;
    residents: { patient_id: string; outcome: string }[];
    /** false: record the residents and leave the review open (the docked entry: the reviewer closes it herself). */
    complete?: boolean;
  }) => apiRequest<TwlDayResult>('POST', `${BASE}/twl-day`, body),

  /** Leads waiting to be placed, one per resident (Isaiah Cotto, 2026-09-30: no automatic distribution). */
  leads: (days?: number) => apiRequest<LeadDesk>('GET', `${BASE}/leads${days ? `?days=${days}` : ''}`),

  /** Place one resident with a PA. Every open copy of the resident moves with it. */
  place: (caseId: string, advocateUserId: string) =>
    apiRequest<{ caseId: string; advocateUserId: string; changed: boolean; swept: number }>('POST', `${BASE}/leads/${caseId}/place`, { advocate_user_id: advocateUserId }),

  coverage: () => apiRequest<CoverageList>('GET', `${BASE}/coverage`),

  /** Who covers a facility. `null` clears it. Setting it confirms it. */
  setCoverage: (facilityId: string, advocateUserId: string | null, reason?: string | null) =>
    apiRequest<{ facilityId: string; advocateUserId: string | null }>('PUT', `${BASE}/coverage/${facilityId}`, { advocate_user_id: advocateUserId, reason: reason ?? null }),

  /**
   * One PA for many buildings, in one transaction (2026-10-26). A building another PA covers is
   * REFUSED (skipped) unless `replace`; an unknown or merged building refuses the whole request.
   */
  bulkCoverage: (body: { facility_ids: string[]; advocate_user_id: string; reason?: string | null; replace?: boolean }) =>
    apiRequest<BulkCoverageResult>('POST', `${BASE}/coverage/bulk`, body),

  /** Residents waiting for a replacement, with their warranty and paid-in-full checks. */
  replacements: () => apiRequest<ReplacementQueue>('GET', `${BASE}/replacements`),

  /** Record one check a person made. A warranty pass carries the end date read in CounselEAR. */
  recordCheck: (patientId: string, body: { gate: ReplacementGate; result: 'PASS' | 'FAIL'; warranty_expires_on?: string | null; note?: string | null }) =>
    apiRequest<{ id: string }>('POST', `${BASE}/replacements/${patientId}/checks`, body),

  /**
   * Place a replacement. The body names nobody: the server places with the PA
   * who covers the resident's facility. Refused with REASON_REQUIRED (no open
   * reason), FACILITY_PA_ASSIGNMENT_REQUIRED (no PA covers the facility) and
   * CHECKS_NOT_PASSED (warranty and paid in full not both confirmed).
   */
  placeReplacement: (patientId: string) =>
    apiRequest<{ requestId: string; caseId: string; opened: boolean; advocateUserId: string; advocate: string | null; facility: string | null; changed: boolean; swept: number }>(
      'POST', `${BASE}/replacements/${patientId}/place`, {}
    ),

  /** Residents to add to the queue, by name or CounselEAR id, within the placer's facilities. */
  searchReplacementResidents: (q: string, page?: number) =>
    apiRequest<{ q: string; total: number; page?: number; limit?: number; items: ReplacementResidentHit[] }>('GET', `${BASE}/replacements/residents?q=${encodeURIComponent(q)}${page && page > 1 ? `&page=${page}` : ''}`),

  /** Any resident in the reviewer's buildings, with where they stand (read-only). */
  findResident: (q: string, page?: number) =>
    apiRequest<{ q: string; total: number; page?: number; limit?: number; items: ResidentFindHit[] }>('GET', `${BASE}/residents/find?q=${encodeURIComponent(q)}${page && page > 1 ? `&page=${page}` : ''}`),

  /**
   * Record a replacement and why, for any resident; the resident then appears
   * in the queue. With `replace`, the open request is cancelled and this one
   * takes its place (the only way a reason is corrected); without it, a
   * resident who already has one open is refused (REQUEST_ALREADY_OPEN).
   */
  requestReplacement: (patientId: string, body: ReplacementReasonInput & { replace?: boolean; idempotency_key?: string }) =>
    apiRequest<{ id: string; state: string; replayed: boolean }>('POST', `${BASE}/replacements/${patientId}/request`, body),

  /** Withdraw the resident's open request, saying why. The record is kept. */
  cancelReplacementRequest: (patientId: string, reason: string) =>
    apiRequest<{ id: string; state: string }>('POST', `${BASE}/replacements/${patientId}/request/cancel`, { reason }),
};

export type ReplacementSide = 'LEFT' | 'RIGHT' | 'BOTH';
export type ReplacementReasonCode = 'LOST' | 'DAMAGED' | 'NOT_WORKING' | 'RECHARGEABLE_UPGRADE' | 'WARRANTY_REPLACEMENT' | 'OTHER';
export type ReplacementFinancialPath = 'WARRANTY' | 'REPLACEMENT_CHARGE' | 'NEW_UMED' | 'PRIVATE_PAY' | 'SPENDDOWN_PLUS_UMED' | 'REVIEW_REQUIRED';

// The vocabulary is the business's (EP-010 ruling R1) and the words are what a person reads.
export const REPLACEMENT_SIDES: ReplacementSide[] = ['LEFT', 'RIGHT', 'BOTH'];
export const REPLACEMENT_REASON_CODES: ReplacementReasonCode[] = ['LOST', 'DAMAGED', 'NOT_WORKING', 'RECHARGEABLE_UPGRADE', 'WARRANTY_REPLACEMENT', 'OTHER'];
export const REPLACEMENT_FINANCIAL_PATHS: ReplacementFinancialPath[] = ['WARRANTY', 'REPLACEMENT_CHARGE', 'NEW_UMED', 'PRIVATE_PAY', 'SPENDDOWN_PLUS_UMED', 'REVIEW_REQUIRED'];
export const REPLACEMENT_SIDE_LABEL: Record<ReplacementSide, string> = { LEFT: 'Left', RIGHT: 'Right', BOTH: 'Both' };
export const REPLACEMENT_REASON_LABEL: Record<ReplacementReasonCode, string> = {
  LOST: 'Lost', DAMAGED: 'Damaged', NOT_WORKING: 'Not working', RECHARGEABLE_UPGRADE: 'Rechargeable upgrade', WARRANTY_REPLACEMENT: 'Warranty replacement', OTHER: 'Other',
};
export const REPLACEMENT_PATH_LABEL: Record<ReplacementFinancialPath, string> = {
  WARRANTY: 'Warranty', REPLACEMENT_CHARGE: 'Replacement charge', NEW_UMED: 'New UMED', PRIVATE_PAY: 'Private pay', SPENDDOWN_PLUS_UMED: 'Spenddown plus UMED', REVIEW_REQUIRED: 'Review required',
};
export const REPLACEMENT_NOTE_MAX = 1000;

/** What a person types. The amount is theirs: eMerge derives none and holds no price list. */
export type ReplacementReasonInput = {
  side: ReplacementSide;
  reason_code: ReplacementReasonCode;
  financial_path: ReplacementFinancialPath;
  requested_amount_cents: number | null;
  note: string | null;
};
export type ReplacementRequest = {
  id: string;
  side: ReplacementSide;
  reason_code: ReplacementReasonCode;
  financial_path: ReplacementFinancialPath;
  requested_amount_cents: number | null;
  note: string | null;
  requested_at: string;
  requested_by: string | null;
};
export type ReplacementResidentHit = {
  id: string;
  name: string | null;
  counselear_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  resident_status: string | null;
  situation_words: string;
  /** Already has an open replacement request or an open replacement case. */
  open_replacement: boolean;
  /** Inside the buildings the caller may place in; false means shown, but not addable by them. */
  in_place_scope: boolean;
};

export type ResidentFindState = 'RESUBMITTAL_PENDING' | 'OPEN_HELD' | 'OPEN_UNASSIGNED' | 'CLOSED' | 'NO_CASE';
export type ResidentFindHit = {
  id: string;
  name: string | null;
  counselear_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  state: ResidentFindState;
  resubmittal_since: string | null;
  latest_case: { stage: string; status_word: string | null; closed_word: string | null; opened_on: string | null; closed_on: string | null; advocate: string | null } | null;
};

/** "Both · Lost · Replacement charge · $500.00 · note": the reason as the server states it. */
export const replacementReasonWords = (r: ReplacementRequest): string => [
  REPLACEMENT_SIDE_LABEL[r.side], REPLACEMENT_REASON_LABEL[r.reason_code], REPLACEMENT_PATH_LABEL[r.financial_path],
  r.requested_amount_cents === null ? null : `$${(r.requested_amount_cents / 100).toFixed(2)}`, r.note,
].filter(Boolean).join(' · ');

/** Whole cents from what a person typed in dollars ("500", "1,500.50"); null when blank; undefined when it is not an amount. */
export function dollarsToCents(text: string): number | null | undefined {
  const t = text.trim().replace(/^\$/, '').replace(/,/g, '');
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

export type ReplacementGate = 'WARRANTY' | 'PAID_IN_FULL';
export type ReplacementCheck = { result: 'PASS' | 'FAIL'; warranty_expires_on: string | null; note: string | null; checked_at: string; checked_by: string | null };
export type Replacement = {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  counselear_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  counselear_tags: string | null;
  on_manifest: string | null;
  /** The day eMerge learned the resident needs a replacement (rule identified-on@1), or null: "Not recorded". */
  identified_on?: string | null;
  identified_source?: 'REQUEST' | 'MANIFEST' | 'EMERGE_CASE' | 'MONDAY_CASE' | null;
  case_id: string | null;
  open_cases: number;
  /** Who holds the resident now. Information only: it does not decide who the replacement goes to. */
  holder_id: string | null;
  holder: string | null;
  /** The open request and its reason, or null: "No reason recorded". */
  request: ReplacementRequest | null;
  /** The PA who covers this resident's facility, or null when none does. Placing goes to this PA and no other. */
  goes_to: { advocate_user_id: string; advocate: string | null; facility_name: string; confirmed: boolean; reason: string | null } | null;
  dispensed_on: string | null;
  ears: string | null;
  warranty_hint: { state: 'IN_WARRANTY' | 'EXPIRED' | 'UNKNOWN'; expires_on: string | null };
  checks: Partial<Record<ReplacementGate, ReplacementCheck>>;
  /** The two checks have passed. */
  ready: boolean;
  missing: string[];
  /** Checks passed, a reason is recorded, and a PA covers the facility. The server decides again on placing. */
  can_place: boolean;
};
export type ReplacementQueue = { items: Replacement[]; canPlace: boolean };

export type Advocate = { id: string; name: string; manager: boolean; open_cases: number };

export type Lead = {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  twl_tag_since: string | null;
  /** The effective TWL-since clock (CounselEAR tag day, else the case's TWL Date, else the day eMerge first saw the case) and which it is. */
  twl_since?: string | null;
  twl_since_source?: 'TAG' | 'TWL_DATE' | 'FIRST_SEEN' | null;
  counselear_lead: boolean;
  recorded: boolean;
  case_id: string;
  intake_exceptions: string[] | null;
  open_cases: number;
  suggested_advocate_id: string | null;
  suggested_advocate: string | null;
  coverage_reason: string | null;
  coverage_source: 'HISTORY' | 'MANAGER' | null;
  coverage_confirmed: boolean;
  /** False when the resident's only open case is finished (DNQ, NITWL, enrolled): not placeable here. */
  workable?: boolean;
  /** episodeTruth for the resident's other episodes; set when not NEW_LEAD_OK. */
  episode?: Episode | null;
  finished_stage?: string | null;
  finished_disposition?: string | null;
  finished_opened_on?: string | null;
  /** D-31: set when a manager reopened a closed lead with nobody to hold it: "Reopened — <reason>". */
  reopened_tag?: string | null;
  /**
   * 2026-10-28, "Prior closure — review": a PA already closed this resident DNQ / NITWL / C-NITWL (`disposition`;
   * `word` the business spelling) on `closed_on`, after `visit_date`, the manifest visit this lead came from.
   * Held on the Lead Desk: not placed from it (leadDesk.place() refuses it, ALREADY_WORKED). `case_id` is sent only
   * when the caller can open the PA workspace (`LeadDesk.can_open_case`). Absent from an older server.
   */
  prior_closure?: { case_id?: string; disposition: string; word: string; closed_on: string; visit_date: string; message: string } | null;
};

export type LeadDesk = {
  items: Lead[];
  /** Leads only the wider window (21-90 days) holds: tagged more than 21 days ago and still unplaced. Absent from an older server. */
  older?: Lead[];
  advocates: Advocate[];
  canPlace: boolean;
  /** 2026-10-28: the caller holds ADVOCACY_LEAD_VIEW, so a held lead's closed case may be linked. Absent from an older server. */
  can_open_case?: boolean;
  days: number;
};

export type Coverage = {
  facility_id: string;
  facility_name: string;
  advocate_user_id: string | null;
  advocate: string | null;
  reason: string | null;
  source: 'HISTORY' | 'MANAGER' | null;
  history_share: string | null;
  confirmed_at: string | null;
  open_cases: number;
  /** 2026-10-26, additive: absent from an older server. */
  open_case_count?: number;
  /** Unmerged residents whose building this is. */
  resident_count?: number;
  /** Open cases in the Unassigned queue at this building (the Unassigned tab's own count). */
  open_unassigned_count?: number;
  /** facilities.status as stored: Active, Cold, Dropped, DONT BUG, Interested; null when none. */
  building_status?: string | null;
  /** Merged into another building: coverage cannot be set on it. */
  merged?: boolean;
  /** Whether a provider could mark TWL Q here today (the provider refusal's own rule), and if not why. */
  twl_q_markable?: boolean;
  twl_q_closure?: { kind: string | null; reason: string | null; source: string } | null;
  twl_q_refusal?: string | null;
};

/** Someone coverage can name: every eligible PA holder, managers included, with their load (2026-10-26). */
export type CoverageHolder = {
  id: string; name: string; manager: boolean; pa_role?: boolean;
  /** False when the PA role is only proposed: shown, but the server will not accept it until ratified. */
  settable: boolean;
  /** Buildings this person covers now. */
  buildings: number;
  open_cases: number;
};

export type CoverageList = { items: Coverage[]; advocates: Advocate[]; holders?: CoverageHolder[]; canEdit: boolean; bulkMax?: number };

export type BulkCoverageOutcome = 'SET' | 'UNCHANGED' | 'REPLACED' | 'REFUSED';
export type BulkCoverageResult = {
  advocateUserId: string;
  advocate: string;
  replace: boolean;
  results: { facility_id: string; outcome: BulkCoverageOutcome; reason: string }[];
  counts: Record<BulkCoverageOutcome, number>;
};

export type FacilityOption = { id: string; name: string; city: string | null; state: string | null };

export type TwlDayResult = {
  reviewId: string;
  facilityId: string;
  visitDate: string;
  recorded: number;
  leadsCreated: number;
  leadsAlready: number;
  notLeads: number;
  refused: number;
  ready: number;
  held: number;
  /** Residents a provider already marked TWL Q at the building that day: their line and lead were kept. */
  providerMarksKept?: number;
  /** 2026-10-26: residents a PA already closed DNQ / NITWL / C-NITWL after the visit day: no new lead. */
  alreadyWorked?: number;
  /** 2026-10-28: whether the day's review closed, and, when a close was asked for and did not happen, why. */
  completed?: boolean;
  complete_refused?: { code: string; message: string } | null;
  results: {
    patientId: string;
    outcome: 'LEAD_CREATED' | 'LEAD_ALREADY_EXISTED' | 'RECORDED_NOT_A_LEAD' | 'INTAKE_REFUSED' | 'LINE_FAILED'
      | 'EVIDENCE_REFUSED' | 'PROVIDER_MARK_KEPT' | 'ALREADY_WORKED' | 'LINE_REFUSED';
    readiness?: ReadinessState;
    heldBy?: string[];
    code?: string;
    /** The refusal in words (ALREADY_SOLD, UMED_IN_FLIGHT, ALREADY_WORKED). */
    why?: string;
    /** ALREADY_WORKED: "A PA already closed this resident as DNQ on 10/26/26; no new lead was opened." */
    message?: string;
    closedAs?: AlreadyWorked['disposition'] | null;
    closedOn?: string | null;
  }[];
};

/**
 * Why a confirmed TWL is held out of distribution, in the business's words.
 *
 * A held lead is not a refused one: it is recorded, it is on the case, and it
 * is waiting on a fact. Saying which fact is the difference between somebody
 * fixing it this morning and nobody knowing it exists.
 */
export const REASON_LABEL: Record<string, string> = {
  MISSING_PATIENT_LIABILITY: 'No patient liability on file',
  PATIENT_LIABILITY_TOO_LOW: 'Liability below the lowest term any product offers',
  IDENTITY_CONFLICT: 'An identity review is open for this resident',
  FACILITY_UNRESOLVED: 'No confirmed building, so nothing can be scoped',
  DUPLICATE_EPISODE_REVIEW: 'This resident already has an open case',
  NO_TEST_ON_FILE: 'No hearing test on file, so the TWL cannot be shown to be current',
  TEST_EVIDENCE_STALE: 'The most recent test is older than the qualifying period',
  TEST_EVIDENCE_AMBIGUOUS: 'No evidence names a date a hearing test happened',
};

export const reasonWords = (code: string): string => REASON_LABEL[code] || code.replace(/_/g, ' ').toLowerCase();
